import { useEffect, useState } from "react";
import { api, fmtDate } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog";
import { Textarea } from "../../components/ui/textarea";
import { toast } from "sonner";
import { ShieldCheck, CheckCircle2, XCircle, Clock, Eye } from "lucide-react";

const STATUS_BG = {
    submitted: "bg-amber-50 text-amber-700 border-amber-200",
    verified:  "bg-emerald-50 text-emerald-700 border-emerald-200",
    declined:  "bg-rose-50 text-rose-700 border-rose-200",
};

export default function AdminKyc() {
    const [tab, setTab] = useState("submitted");
    const [rows, setRows] = useState([]);
    const [viewOpen, setViewOpen] = useState(false);
    const [active, setActive] = useState(null);
    const [activeFull, setActiveFull] = useState(null);
    const [decision, setDecision] = useState("verified");
    const [note, setNote] = useState("");
    const [busy, setBusy] = useState(false);

    const load = (status) => api.get(`/kyc/admin/submissions${status === "all" ? "" : `?status=${status}`}`).then((r) => setRows(r.data));
    useEffect(() => { load(tab); }, [tab]);

    const openSubmission = async (sub) => {
        setActive(sub);
        setDecision("verified");
        setNote("");
        try {
            const r = await api.get(`/kyc/admin/submissions/${sub.id}`);
            setActiveFull(r.data);
        } catch (err) {
            toast.error("Failed to load submission");
            return;
        }
        setViewOpen(true);
    };

    const decide = async () => {
        if (decision === "declined" && !note) return toast.error("Reason required for decline");
        setBusy(true);
        try {
            await api.post(`/kyc/admin/submissions/${active.id}/decide`, { decision, reason: note || undefined });
            toast.success(`KYC ${decision}`);
            setViewOpen(false);
            load(tab);
        } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
        finally { setBusy(false); }
    };

    return (
        <div data-testid="admin-kyc-page">
            <div className="flex items-end justify-between mb-6">
                <div>
                    <h1 className="font-display text-2xl font-semibold text-[#0A1930] flex items-center gap-2">
                        <ShieldCheck size={22} className="text-[#0066FF]" /> KYC Verification
                    </h1>
                    <p className="text-sm text-slate-500 mt-1">{rows.length} submissions in this bucket</p>
                </div>
            </div>

            <Tabs value={tab} onValueChange={setTab} className="mb-4">
                <TabsList className="bg-white border border-slate-200 rounded-xl p-1" data-testid="kyc-filter">
                    <TabsTrigger value="submitted">Pending review</TabsTrigger>
                    <TabsTrigger value="verified">Verified</TabsTrigger>
                    <TabsTrigger value="declined">Declined</TabsTrigger>
                    <TabsTrigger value="all">All</TabsTrigger>
                </TabsList>
            </Tabs>

            <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden product-shadow">
                <div className="overflow-x-auto admin-scroll">
                    <table className="w-full text-sm">
                        <thead className="bg-slate-50">
                            <tr className="text-left text-xs uppercase tracking-wider font-bold text-slate-500">
                                <th className="px-5 py-3">User</th>
                                <th className="px-5 py-3">ID Type</th>
                                <th className="px-5 py-3">ID Number</th>
                                <th className="px-5 py-3">Submitted</th>
                                <th className="px-5 py-3">Status</th>
                                <th className="px-5 py-3">Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.length === 0 && (
                                <tr><td colSpan={6} className="px-5 py-12 text-center text-sm text-slate-400">No submissions in this bucket</td></tr>
                            )}
                            {rows.map((s) => (
                                <tr key={s.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`kyc-row-${s.id}`}>
                                    <td className="px-5 py-3 font-medium">
                                        <div>{s.user_full_name}</div>
                                        <div className="text-[10px] text-slate-500 font-mono">{s.user_email}</div>
                                    </td>
                                    <td className="px-5 py-3 capitalize"><Badge variant="outline">{(s.id_type || "").replace("_", " ")}</Badge></td>
                                    <td className="px-5 py-3 font-mono text-xs">{s.national_id}</td>
                                    <td className="px-5 py-3 text-xs text-slate-500">{fmtDate(s.submitted_at)}</td>
                                    <td className="px-5 py-3"><Badge className={`capitalize border ${STATUS_BG[s.status] || "bg-slate-100"}`}>{s.status}</Badge></td>
                                    <td className="px-5 py-3">
                                        <Button onClick={() => openSubmission(s)} size="sm" variant="outline" className="h-7 text-xs" data-testid={`view-${s.id}`}>
                                            <Eye size={12} /> Review
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            <Dialog open={viewOpen} onOpenChange={(v) => !v && setViewOpen(false)}>
                <DialogContent className="rounded-2xl max-w-3xl">
                    <DialogHeader>
                        <DialogTitle className="font-display flex items-center gap-2"><ShieldCheck size={18} className="text-[#0066FF]" /> Review KYC — {activeFull?.user_full_name}</DialogTitle>
                        <DialogDescription>Compare the selfie against the ID. Approve to verify the user (+80 score), or decline with a reason.</DialogDescription>
                    </DialogHeader>
                    {activeFull && (
                        <div className="space-y-4">
                            <div className="grid grid-cols-3 gap-3">
                                <ImagePane label="Selfie" b64={activeFull.selfie_b64} />
                                <ImagePane label="ID front" b64={activeFull.id_front_b64} />
                                <ImagePane label="ID back" b64={activeFull.id_back_b64} />
                            </div>
                            <div className="grid grid-cols-2 gap-3 text-sm">
                                <Field label="ID Type" value={(activeFull.id_type || "").replace("_", " ")} />
                                <Field label="ID Number" value={activeFull.national_id} mono />
                                <Field label="Date of birth" value={activeFull.dob || "—"} />
                                <Field label="Address" value={activeFull.address || "—"} />
                            </div>

                            {activeFull.status === "submitted" && (
                                <div className="space-y-3 pt-3 border-t border-slate-100">
                                    <div className="flex gap-2">
                                        <Button onClick={() => setDecision("verified")} variant={decision === "verified" ? "default" : "outline"} size="sm" className={decision === "verified" ? "bg-emerald-600 hover:bg-emerald-700" : ""} data-testid="dec-verify">
                                            <CheckCircle2 size={14} /> Approve
                                        </Button>
                                        <Button onClick={() => setDecision("declined")} variant={decision === "declined" ? "default" : "outline"} size="sm" className={decision === "declined" ? "bg-rose-600 hover:bg-rose-700" : "border-rose-200 text-rose-600"} data-testid="dec-decline">
                                            <XCircle size={14} /> Decline
                                        </Button>
                                    </div>
                                    <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder={decision === "declined" ? "Required: reason for decline" : "Optional approval note"} className="rounded-xl" data-testid="kyc-note-input" />
                                    <Button onClick={decide} disabled={busy} className={`w-full h-11 rounded-xl ${decision === "verified" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-rose-600 hover:bg-rose-700"}`} data-testid="kyc-decide-btn">
                                        {busy ? "Processing…" : decision === "verified" ? "Approve KYC" : "Decline KYC"}
                                    </Button>
                                </div>
                            )}

                            {activeFull.decision_reason && (
                                <div className="bg-slate-50 rounded-xl p-3 text-xs">
                                    <div className="font-semibold">Previous decision: {activeFull.status}</div>
                                    <div className="text-slate-600 mt-1">{activeFull.decision_reason}</div>
                                </div>
                            )}
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}

function ImagePane({ label, b64 }) {
    return (
        <div className="bg-slate-50 rounded-xl p-2">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1.5">{label}</div>
            <div className="aspect-square rounded-lg overflow-hidden bg-slate-200 flex items-center justify-center">
                {b64 ? (
                    <img src={b64} alt={label} className="w-full h-full object-cover" />
                ) : (
                    <div className="text-[10px] text-slate-400">Not provided</div>
                )}
            </div>
        </div>
    );
}

function Field({ label, value, mono }) {
    return (
        <div className="bg-slate-50 rounded-lg p-2">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">{label}</div>
            <div className={`mt-0.5 ${mono ? "font-mono text-xs" : "text-sm font-semibold"}`}>{value}</div>
        </div>
    );
}
